import React from 'react';
import * as yup from 'yup';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Popper,
  Paper,
  Grid,
  Button,
} from '@mui/material';
import CrossIcon from 'components/SVG/CrossIcon';
import { SparkForm } from 'sharedComponents/SparkForm';
import { useResourceAccessLevels } from 'hooks/share/useResourceAccessLevels';
import { useResourceShares } from 'hooks/share/useResourceShare';
import SparkResourceShareContent from './SparkResourceShareContent';
import { useBatchAddResourceShare } from 'hooks/share/useBatchAddResourceShare';
import { ResourceShare } from 'types/Share/ResourceShares';
import { useShareTypes } from 'hooks/share/useShareTypes';
import { SparkButton } from 'sharedComponents/UI';
import { BackIcon } from 'components/SVG';
import ManageCollaborator from './ManageCollaborator';
import { FormikHelpers } from 'formik';
import { useResourceTypes } from 'hooks/EntityTypes/useResourceTypes';
import { PopperProps } from '@mui/material/Popper/BasePopper.types';

const validationSchema = yup.object().shape({
  individualEmails: yup.array(yup.string().email('Please enter a valid email')),
  individualAccessLevelID: yup.string().required(),
  inviteMessage: yup.string().required('Please enter a custom invite message'),

  accessLink: yup.string(),
  accessLevelIDLink: yup.string(),
  tags: yup.array(),
  url: yup.string(),
});

export type SparkResourceSharePopoverValues = {
  individualEmails: string[];
  individualAccessLevelID: string;
  inviteMessage: string;

  isActive: boolean;
  shareAbleLinkAccessLevel: string;
  url: string;
} & Record<string, string | string[] | boolean>;
const BASE_URL = 'https://uat-sparkup-frontend.azurewebsites.net';

type SparkResourceSharePopoverProps = {
  resourceID: string;
  resourceTypeID: string;
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  placement?: PopperProps['placement'];
};

function SparkResourceSharePopover({
  resourceID,
  resourceTypeID,
  open,
  onClose,
  anchorEl,
  placement = 'bottom-end',
}: SparkResourceSharePopoverProps) {
  const { resourceShares: allResourceShares } = useResourceShares(
    resourceID,
    resourceTypeID
  );
  const { mutateAsync: addResourceShares } = useBatchAddResourceShare();
  const { idByNameMap } = useShareTypes();
  const [isCollaboratorView, setIsCollaboratorView] = React.useState(false);
  const { getAccessLevelIDByResourceAccessLevelID, getResourceAccessLevelID } =
    useResourceAccessLevels(resourceTypeID);
  const { keyByIDMap } = useResourceTypes();

  const [accessLevelShares, sharableLinkShares] =
    allResourceShares?.reduce<ResourceShare[][]>(
      (acc, curr) => {
        if (!idByNameMap) {
          return acc;
        }
        if (curr.attributes.shareTypeID === +idByNameMap['Link']) {
          acc[1].push(curr);
          return acc;
        }
        acc[0].push(curr);

        return acc;
      },
      [[], []]
    ) || [];

  const { accessLevelDropdownOptions } =
    useResourceAccessLevels(resourceTypeID);

  const accessLevelIDForView = accessLevelDropdownOptions.find(
    (item) => item.label === 'View'
  )?.value as string;

  const accessLevelSharesInitialValues = accessLevelShares?.reduce<
    Record<string, string>
  >((acc, curr) => {
    return {
      ...acc,
      [curr.id]:
        getAccessLevelIDByResourceAccessLevelID(
          curr.attributes.resourceAccessLevelID
        ) || '',
    };
  }, {});

  const shareableURL = React.useMemo(() => {
    const resource = keyByIDMap?.[resourceTypeID || ''];
    if (!resource) {
      return '';
    }
    return `${BASE_URL}/${resource}/${resourceID}`;
  }, [keyByIDMap, resourceID, resourceTypeID]);

  const initialValues: SparkResourceSharePopoverValues = {
    individualEmails: [],
    individualAccessLevelID: accessLevelIDForView || '',
    inviteMessage: '',

    isActive: sharableLinkShares?.[0]?.attributes.isActive || false,
    shareAbleLinkAccessLevel:
      getAccessLevelIDByResourceAccessLevelID(
        sharableLinkShares?.[0]?.attributes.resourceAccessLevelID
      ) ||
      accessLevelIDForView ||
      '',
    url: shareableURL || '',
    ...accessLevelSharesInitialValues,
  };

  const handleSubmit = async (
    values: SparkResourceSharePopoverValues,
    formikHelpers: FormikHelpers<SparkResourceSharePopoverValues>
  ) => {
    // access link submit only
    const { individualEmails, inviteMessage, individualAccessLevelID } = values;

    const updates = individualEmails?.map((email) => {
      return {
        resourceID: resourceID,
        resourceTypeID: resourceTypeID,
        resourceAccessLevelID: getResourceAccessLevelID(
          resourceTypeID,
          individualAccessLevelID
        ),
        email: email,
        inviteMessage: inviteMessage,
        // inviteStatusTypeID: '',
        shareTypeID: idByNameMap ? +idByNameMap?.['Individual'] : 1,
      };
    });

    await addResourceShares({ resourceShares: updates });
    formikHelpers.resetForm();
  };

  const handleClosePopOver = () => {
    setIsCollaboratorView(false);
    onClose();
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      disablePortal={false}
      placement={placement}
      sx={{ top: '5px !important', maxWidth: '40rem', zIndex: 1000 }}
    >
      <Paper elevation={4}>
        <Box
          display={'flex'}
          justifyContent='space-between'
          alignItems='center'
          p={2}
        >
          <Box display='flex' alignItems='center' gap={1}>
            {isCollaboratorView && (
              <Button
                onClick={() => setIsCollaboratorView(false)}
                sx={{
                  minWidth: 'unset',
                  width: '40px',
                  height: '40px',
                  padding: 0,
                  borderWidth: '1px',
                  borderRadius: '14px',
                  borderColor: '#EEEEEE',
                }}
                variant='outlined'
              >
                <BackIcon />
              </Button>
            )}
            <Typography
              sx={{
                color: 'black',
                fontFamily: 'Mont-SemiBold',
                fontSize: '18px',
              }}
            >
              {isCollaboratorView ? 'Manage Collaborators' : 'Share'}
            </Typography>
          </Box>
          <IconButton onClick={handleClosePopOver}>
            <CrossIcon width='24' height='24' stroke='black' />
          </IconButton>
        </Box>
        <Divider sx={{ width: '100%' }} />
        <SparkForm<SparkResourceSharePopoverValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSave={handleSubmit}
        >
          {isCollaboratorView ? (
            <>
              <ManageCollaborator
                resourceTypeID={resourceTypeID}
                resourceID={resourceID}
                accessLevelShares={accessLevelShares}
              />
              <Divider sx={{ width: '100%' }} />
              <Grid
                item
                container
                xs={12}
                padding={2}
                paddingX={3}
                justifyContent='flex-end'
              >
                <SparkButton onClick={handleClosePopOver} variant='contained'>
                  Done
                </SparkButton>
              </Grid>
            </>
          ) : (
            <SparkResourceShareContent
              accessLevelDropdownOptions={accessLevelDropdownOptions}
              sharableLinkShares={sharableLinkShares}
              onManageClick={() => setIsCollaboratorView(true)}
              resourceTypeID={resourceTypeID}
              resourceID={resourceID}
              accessLevelShares={accessLevelShares}
            />
          )}
        </SparkForm>
      </Paper>
    </Popper>
  );
}

export default SparkResourceSharePopover;
